import { useState, useEffect, useRef } from "react";
import { getParams } from "../../../utils/getParams";
import { TAB_ENUM } from "../../common/config";
import { getDynamicTabImage, getMediaDynamicTab } from "../../../utils/getDynamicTab";
import { transformMediaDataToItems } from "../../../utils/transformMediaData";
import { v4 as uuidv4 } from 'uuid';
import { getCategory } from "../../../utils/utils";
import useWindowSize from "../../../utils/useWindowSize";
import { isHeroImageModeEnabled, generateHeroImageCarouselItems } from "../../../utils/heroImageUtils";

const useMediaDataAndTabs = () => {
    const [skuData, setSkuData] = useState({});
    const [skuName, setSkuName] = useState('');
    const [loading, setLoading] = useState(true);
    const [dealerVinId, setDealerVinId] = useState(null);
    const [enterpriseId, setEnterpriseId] = useState(null);
    const [aspectRatio, setAspectRatio] = useState('16/9');
    const [mediaId, setMediaId] = useState(null);
    const [teamId, setTeamId] = useState(null);
    const [executionTime, setExecutionTime] = useState(0)
    const [apiCallFlag, setApiCallFlag] = useState('json');
    const [isFirstSixExt, setIsFirstSixExt] = useState(false);
    const [productFetchTimes, setProductFetchTimes] = useState({
        spin: 0,
        image: 0,
        video: 0
    });
    const queryParams = new URLSearchParams(window.location.search);
    const isDemo = queryParams.get('demo');
    const [isMobile, setIsMobile] = useState(false);
    const [postMessageData, setPostMessageData] = useState(null);
    const [heroImagePostMessageData, setHeroImagePostMessageData] = useState(null);
    const [isHeroImageMode, setIsHeroImageMode] = useState(false);
    const [heroImageIndex, setHeroImageIndex] = useState(null);

    const [selectedTemplate, setSelectedTemplate] = useState("t1");

    const [selectedTab, setSelectedTab] = useState(TAB_ENUM.THREE_SIXTY_VIEW);
    const [tabs, setTabs] = useState([]);

    //carousel items
    const [carouselItems, setCarouselItems] = useState([]);
    const [dynamicTabImage, setDynamicTabImage] = useState([]);
    const { params } = getParams();

    const sessionIdRef = useRef(null);
    const isApiCalledRef = useRef(false);
    const processedPostMessageRef = useRef(null);
    const windowSize = useWindowSize();

    //setLoading to false if hero image mode is enabled
    useEffect(() => {
        if (isHeroImageMode) {
            setLoading(false);
        }
    }, [isHeroImageMode]);

    if (!sessionIdRef.current) {
        const storedSessionId = sessionStorage.getItem('sessionId');
        if (storedSessionId) {
            sessionIdRef.current = storedSessionId;
        } else {
            const newSessionId = "session" + uuidv4();
            sessionIdRef.current = newSessionId;
            sessionStorage.setItem('sessionId', newSessionId);
        }
    }

    useEffect(() => {
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: 'IFRAME_READY' }, '*');
          }
        const handlePostMessage = (event) => {
          const { data } = event;
          if (!data || typeof data !== 'object') return;
      
          // ✅ Ignore Webpack / Next.js dev messages
          if (data.type && data.type.startsWith('webpack')) return;
      
          // ✅ Handle only your messages
          if (data.type === 'MEDIA_DATA_READY') {
            // console.log('[DEBUG] Valid MEDIA_DATA_READY received:', data);
            setPostMessageData(data);
          }
          if (data.type === 'HERO_IMAGE_DATA_READY') {
            // console.log('[DEBUG] Valid HERO_IMAGE_DATA_READY received:', data);
            setHeroImagePostMessageData(data);
          } 
        };
      
        window.addEventListener('message', handlePostMessage);
        return () => window.removeEventListener('message', handlePostMessage);
      }, []);
      

    const fetchMediaFromStorage = async ({ mediaId, product }) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_MEDIA_BUCKET_URL_V3}/m/o/${mediaId}/${product}.json`);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching media data:', error);
            throw error;
        }
    }
    const fetchMediaFromStorageV3 = async ({ enterpriseId, skuName, product }) => {
        const startTime = performance.now();
        try {
            let URL = `${process.env.REACT_APP_MEDIA_BUCKET_URL_V3}/common-iframe-sku/${skuName}_${enterpriseId}/${product}.json`
            const response = await fetch(URL);
            const data = await response.json();
            const endTime = performance.now();
            const fetchTime = endTime - startTime;
            
            const productKey = product === 'threeSixty' ? 'spin' : 
                              product === 'image' ? 'image' : 
                              product === 'video' ? 'video' : null;
            
            if (productKey) {
                setProductFetchTimes(prev => ({
                    ...prev,
                    [productKey]: fetchTime
                }));
            }
            
            let keys;
            if (product === 'image') {
                let imageData = data?.imageData;
                if (imageData) {
                    keys = Object.keys(imageData);
                }
                if (keys?.length > 0) {
                    return data;
                }
                else throw new Error('No data found');
            }
            else return data;

        } catch (error) {
            console.error('Error fetching media data:', error);
            throw error;
        }
    }
    const getDataFromJson = async ({ params, spin, catalog, feature_video }) => {
        isApiCalledRef.current = true;
        try {
            const mediaId = params?.mediaId;
            const skuName = params?.skuName;
            const enterpriseId = params?.enterpriseId;
            if (!mediaId && (!skuName || !enterpriseId)) return;

            const promise = []
            const productsPicked = [];
            if (spin) {
                if (mediaId) {
                    promise.push(fetchMediaFromStorage({ mediaId, product: 'spin' }))
                } else {
                    promise.push(fetchMediaFromStorageV3({ enterpriseId, skuName, product: 'threeSixty' }))
                }
                productsPicked.push('360')
            }
            if (catalog) {
                if (mediaId) {
                    promise.push(fetchMediaFromStorage({ mediaId, product: 'catalog' }))
                } else {
                    promise.push(fetchMediaFromStorageV3({ enterpriseId, skuName, product: 'image' }))
                }
                productsPicked.push('image')
            }
            if (feature_video) {
                if (mediaId) {
                    promise.push(fetchMediaFromStorage({ mediaId, product: 'feature_video' }))
                } else {
                    promise.push(fetchMediaFromStorageV3({ enterpriseId, skuName, product: 'video' }))
                }
                productsPicked.push('video')
            }
            const data = await Promise.all(promise);
            return {
                data: data,
                productsPicked: productsPicked
            };
        } catch (error) {
            console.error('Error fetching media data here:', error);
            return {
                data: null,
                productsPicked: []
            };
        }
    }
    const getDataFromPostMessage = ({ postMessageData, spin, catalog, feature_video }) => {
        try {
            const productsPicked = [];
            const respData = [];
            
            
            if (spin && postMessageData?.data?.spin) {
                respData.push(postMessageData.data.spin);
                productsPicked.push('360');
            }
            if (catalog && postMessageData?.data?.catalog) {
                respData.push(postMessageData.data.catalog);
                productsPicked.push('image');
            }
            if (feature_video && postMessageData?.data?.video) {
                respData.push(postMessageData.data.video);
                productsPicked.push('video');
            }
            if(heroImagePostMessageData?.data?.hero_image) {
                respData.push(heroImagePostMessageData.data.hero_image);
                productsPicked.push('hero_image');
            }
            
            return { data: respData, productsPicked };
        } catch (error) {
            console.error('Error processing postMessage data:', error);
            return { data: null, productsPicked: [] };
        }
    }

    const getDataFromFallback = async ({ params, spin, catalog, feature_video }) => {
        try {
            let mediaId = params?.mediaId;
            let skuName = params?.skuName;
            let enterpriseId = params?.enterpriseId;
            if (!mediaId && (!skuName || !enterpriseId)) return;
            const productsPicked = [];
            let URL;
            if (mediaId) {
                URL = `${process.env.REACT_APP_BASEURL_API_SPYNE}/api/pv1/media-layer/processed-products?mediaId=${mediaId}`;
            } else {
                URL = `${process.env.REACT_APP_BASEURL_API_SPYNE}/api/pv1/media-layer/processed-products?skuName=${skuName}&enterpriseId=${enterpriseId}`;
            }
            const response = await fetch(URL);
            const data = await response.json();

            let respData = [];
            if (spin && data?.spin) {
                respData.push(data.spin);
                productsPicked.push('360');
            }
            if (catalog && data?.catalog && data?.catalog?.imageData && Object.keys(data?.catalog?.imageData)?.length > 0) {
                respData.push(data.catalog);
                productsPicked.push('image');
            }
            if (feature_video && (data?.featureVideo || data?.feature_video)) {
                respData.push(data.featureVideo || data?.feature_video);
                productsPicked.push('video');
            }
            return { data: respData, productsPicked };
        } catch (error) {
            console.error('Error fetching media data:', error);
            return { data: null, productsPicked: [] };
        }
    }
    const checkFirstSixExt = (images) => {
        if(!images || images?.length === 0) return true;
            if(images?.length >= 6){
                //check if first 6 images are exterior images
                const firstSixImages = images.slice(0, 6);
                const isFirstSixExt = firstSixImages.every(image => ((image?.image_category?.toLowerCase() === 'exterior') || (image?.image_category?.toLowerCase() === '360_exterior')));
                return isFirstSixExt;
            }
    
        else return false;
    }
    const createImageItems = (images) => {
        const exterior = images?.filter(
          (item) =>
            getCategory(item?.image_category) === "exterior" ||
            getCategory(item?.image_category) === "360_exterior"
        );
        const interior = images?.filter(
          (item) => getCategory(item?.image_category) === "interior"
        );
        const additional = images?.filter(
          (item) =>
            getCategory(item?.image_category) !== "exterior" &&
            getCategory(item?.image_category) !== "interior" &&
            getCategory(item?.image_category) !== "360_exterior"
        );
        let imageData = {};
        if (exterior?.length > 0) {
          imageData.exterior = [...exterior];
        }
        if (interior?.length > 0) {
          imageData.interior = [...interior];
        }
        if (additional?.length > 0) {
          imageData.miscellaneous = [...additional];
        }
        return imageData;
      };

    const fetchMediaData = async ({ params, spin = 0, catalog = 0, feature_video = 0, isMobile = false}) => {
        const startTime = performance.now();
        try {
 
            let mediaId = params?.mediaId;
            let skuName = params?.skuName;
            let enterpriseId = params?.enterpriseId;
            if (!mediaId && (!skuName || !enterpriseId)) return;
            if (!spin && !catalog && !feature_video) return;

            let isMediaPresent = false;

            let resp;
            let data;
            let productsPicked;

            // Check if postMessage data is available first
            if (postMessageData && postMessageData.type === 'MEDIA_DATA_READY') {
             
                resp = getDataFromPostMessage({ postMessageData, spin, catalog, feature_video });
                data = resp?.data;
                productsPicked = resp?.productsPicked;
                setApiCallFlag('postmessage');
            } else if (!isApiCalledRef.current) {
                // Fallback to original logic
                resp = await getDataFromJson({ params, spin, catalog, feature_video });
                data = resp?.data;
                productsPicked = resp?.productsPicked;
                
                if (!isDemo && (!data || data.length === 0 || data.every(item => item === null))) {
                    resp = await getDataFromFallback({ params, spin, catalog, feature_video });
                    data = resp?.data;
                    productsPicked = resp?.productsPicked;
                    setApiCallFlag('api');
                }
            }
            const mediaData = {
                '360': null,
                'image': null,
                'video': null,
                '360Int': null
            }
            // Function to get image dimensions
            const getImageDimensions = (url) => {
                let newUrl = `https://media.spyneai.com/unsafe/filters:format(webp)/` + url;
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        resolve({
                            width: img.width,
                            height: img.height
                        });
                    };
                    img.onerror = () => {
                        resolve({ width: 16, height: 9 }); // default on error
                    };
                    img.src = newUrl;
                });
            };

            // Function to determine closest aspect ratio (4:3 or 16:9)
            const getClosestAspectRatio = (width, height) => {
                if (!width || !height) return '16/9';

                const actualRatio = width / height;
                const ratio16_9 = 16 / 9;  // ≈ 1.778
                const ratio4_3 = 4 / 3;    // ≈ 1.333

                // Calculate which ratio is closer
                const diff16_9 = Math.abs(actualRatio - ratio16_9);
                const diff4_3 = Math.abs(actualRatio - ratio4_3);

                return diff16_9 < diff4_3 ? '16/9' : '4/3';
            };
            // Function to get aspect ratio from images
            const getAspectRatioFromImages = async (imageData) => {
                if (!imageData) return '16/9';

                // Try exterior images first
                if (imageData?.exterior?.[0]?.output_image_hres_url || imageData?.['360_exterior']?.[0]?.output_image_hres_url) {
                    const dimensions = await getImageDimensions(imageData?.exterior?.[0]?.output_image_hres_url || imageData?.['360_exterior']?.[0]?.output_image_hres_url);
                    if (dimensions) {
                        let aspect = getClosestAspectRatio(dimensions.width, dimensions.height);
                        return aspect;
                    }
                }
                return '16/9'; // Default if no images available
            };

            const getAspectRatioFromSpinImage = async (imageUrl) => {
                if (!imageUrl) return '16/9';
                const dimensions = await getImageDimensions(imageUrl);
                if (dimensions) {
                    return getClosestAspectRatio(dimensions.width, dimensions.height);
                }
                return '16/9';
            }

            const teamConfig = {
                "360": {
                    "isActive": spin ? true : false,
                    "position": spin
                },
                "image": {
                    "isActive": catalog ? true : false,
                    "position": catalog
                },
                "video": {
                    "isActive": feature_video ? true : false,
                    "position": feature_video
                }
            }
            let checkFirstSix = true;

            for (let i = 0; i < data.length; i++) {
                if (data[i]) {
                    isMediaPresent = true;
                    if (productsPicked[i] === '360') {
                        // if (data[i]?.skuId) {
                        //     // const hotspotData = await getHotspotData(data[i]?.skuId);
                        //     // if (hotspotData && hotspotData?.hotspots?.length) {
                        //     //     newData = {
                        //     //         ...data[i],
                        //     //         hotspots: hotspotData?.hotspots?.[0]
                        //     //     }
                        //     // }
                        // }
                        if(data[i]?.interior360Data?.length > 0){
                            mediaData['360Int'] = data[i]?.interior360Data;
                        }
                        mediaData['360'] = data[i];

                    } else if (productsPicked[i] === 'image') {
                        let images = data[i]?.imageData;
                        checkFirstSix = checkFirstSixExt(images);
                        if(checkFirstSix){
                            mediaData['image'] = createImageItems(images);
                        }else{
                            mediaData['image'] = images;
                        }
                    } else if (productsPicked[i] === 'video') {
                        mediaData['video'] = data[i]
                        // @todo: remove this fixed iframe url after testing 
                        let videoId = data[i]?.video_id || data[i]?.videoData?.[0]?.video_id;
                        const skuId = data[i]?.sku_id;
                        if(skuId?.length > 0){
                            mediaData['video'].iFrameUrl = `${process.env.REACT_APP_VIDEO_IFRAME_DOMAIN}/video?sku_id=${skuId}&notVerified${isDemo ? '&view_mode_switcher' : ''}`
                        }else if (videoId) {
                            mediaData['video'].iFrameUrl = `${process.env.REACT_APP_VIDEO_IFRAME_DOMAIN}/video?video_id=${videoId}&notVerified`
                        } else {
                            mediaData['video'].iFrameUrl = null;
                        }
                    }
                    if (data[i]?.dealerVinId) {
                        setDealerVinId(data[i]?.dealerVinId);
                    }
                    if (data[i]?.enterpriseId) {
                        setEnterpriseId(data[i]?.enterpriseId);
                    }
                    if (data[i]?.teamId) {
                        setTeamId(data[i]?.teamId);
                        sessionStorage.setItem('teamId', data[i]?.teamId);
                    }
                }
            }
            setIsFirstSixExt(checkFirstSix);

            // Set aspect ratio based on available images
            let aspectRatio = '16/9';
            if (productsPicked.includes('360') && mediaData['360']) {
                aspectRatio = await getAspectRatioFromSpinImage(mediaData?.['360']?.skuDetails[0]?.output_image_hres_url);
            }else if(productsPicked.includes('image') && mediaData.image && (mediaData.image.exterior?.[0]?.output_image_hres_url || mediaData.image['360_exterior']?.[0]?.output_image_hres_url)) {
                aspectRatio = await getAspectRatioFromImages(mediaData.image);
            }  
            setAspectRatio(aspectRatio);

            mediaData['team_config'] = teamConfig
            mediaData['isMediaPresent'] = isMediaPresent;
            
            //store all media data inside skuData
            setSkuData(mediaData)
           
            // Create items array for carousel
            const items = transformMediaDataToItems(mediaData, checkFirstSix, isMobile);
            const idx360 = items.findIndex(item => item.type === "360");
            const idx360Int = items.findIndex(item => item.type === "360Int");
            if (idx360 !== -1 && idx360Int !== -1 && idx360Int !== idx360 + 1) {
                const [intItem] = items.splice(idx360Int, 1);
                items.splice(idx360 + 1, 0, intItem);
            }

            setCarouselItems(items);
            
            //set visible tabs and selected tab based on team config //false because image has no category
      const tabsBasedOnConfig =
        getMediaDynamicTab(mediaData, checkFirstSix) || [];
            setTabs(tabsBasedOnConfig);
      if (checkFirstSix) {
                setDynamicTabImage(getDynamicTabImage(mediaData.image));
            }
            if (tabsBasedOnConfig.length > 0) {
                setSelectedTab(tabsBasedOnConfig[0]?.key);
            }

        } catch (error) {
            console.error('Error fetching media data:', error);
        } finally {
            const endTime = performance.now();
            const executionTime = endTime - startTime;
            
            setLoading(false);
            setExecutionTime(executionTime);
        }
  };

    const getHeroImageUrl = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_MEDIA_BUCKET_URL_V3}/hero-image-media/${params?.enterpriseId}_${params?.skuName}_${params?.teamId}.json`);
            const data = await response.json();
            return data ? data : '';
        } catch (error) {
            console.error('Error fetching hero image:', error);
            return '';
        }
    }

    // Handle hero image mode - creates shimmer placeholders with hero image at index 1
    const handleHeroImageMode = async () => {
        let heroImageUrl = null;
        let mediaId = null;
        let aspectRatioSpin = null;
        let aspectRatioImage = null;
        const startTime = performance.now();
        try { 
            if(heroImagePostMessageData?.data?.heroImage?.hero_image) {
                heroImageUrl = postMessageData?.data?.heroImage?.hero_image;
                mediaId = heroImagePostMessageData?.data?.heroImage?.mediaId;
                aspectRatioSpin = heroImagePostMessageData?.data?.heroImage?.aspectRatioSpin;
                aspectRatioImage= heroImagePostMessageData?.data?.heroImage?.aspectRatioImage;
            }   
            else {
                // const data = {}
                const data = await getHeroImageUrl();
                heroImageUrl = data?.hero_image;
                mediaId = data?.mediaId;
                aspectRatioSpin = data?.aspectRatioSpin;
                aspectRatioImage = data?.aspectRatioImage;
            }
            const heroCarouselItems = generateHeroImageCarouselItems(params, heroImageUrl);
            
            // Set hero image index (the position where the real image is placed)
            const heroIndex = 1; // This should match the heroImageIndex in heroImageUtils
            setHeroImageIndex(heroIndex);
            
            // Set carousel items
            setCarouselItems(heroCarouselItems);
            
            // Create minimal skuData for hero image mode
            const heroSkuData = {
                image: {
                    exterior: heroCarouselItems.filter(item => !item.isShimmer).map(item => item.data)
                },
                team_config: {
                    image: {
                        isActive: true,
                        position: 1
                    }
                },
                isMediaPresent: true
            };
            
            setSkuData(heroSkuData);
            
            // Set tabs for image view only
            const heroTabs = [{
                key: TAB_ENUM.GALLERY_VIEW,
                label: 'Images',
                isActive: true
            }];
            setTabs(heroTabs);
            setSelectedTab(TAB_ENUM.GALLERY_VIEW);
            
            // Set aspect ratio
            setAspectRatio(aspectRatioSpin || aspectRatioImage || '16/9');
            
        } catch (error) {
            console.error('Error in hero image mode:', error);
        } finally {
            const endTime = performance.now();
            const executionTime = endTime - startTime;
            
            // Loading is already set to false immediately when hero image mode is detected
            setExecutionTime(executionTime);
        }
    };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const isMobParam = queryParams.get('isMob');
    const deviceWidth = windowSize.deviceWidth;
    const shouldBeMobile =
      isMobParam === 'true' ||
      (deviceWidth < 480 && (isMobParam === 'true' || isMobParam == null));
    setIsMobile(!!shouldBeMobile);
  }, [windowSize]);

    // Separate effect for initial data loading - only runs once when params are available
    useEffect(() => {
        if (params?.mediaId || (params?.skuName && params?.enterpriseId)) {
            setSkuName(params?.skuName || '');
            setEnterpriseId(params?.enterpriseId || '');
            setMediaId(params?.mediaId || '');
            
            // Check if hero image mode is enabled
            const heroImageMode = isHeroImageModeEnabled(params);
            setIsHeroImageMode(heroImageMode);
            
            if (heroImageMode) {
                // Handle hero image mode
                handleHeroImageMode();
            } else {
                // Normal mode - fetch media data as usual
                fetchMediaData({ 'params': params, 'spin': Number(params?.spin), 'catalog': Number(params?.catalog), 'feature_video': Number(params?.feature_video), 'isMobile': isMobile });
            }
        }
        if (params?.template === "t2") {
            setSelectedTemplate("t2");
        } else {
            setSelectedTemplate("t1");
        }
    }, []); // Only run once on mount

    // Separate effect for handling postMessage data changes
    useEffect(() => {
        if (postMessageData && postMessageData.type === 'MEDIA_DATA_READY' && 
            (params?.mediaId || (params?.skuName && params?.enterpriseId)) &&
            processedPostMessageRef.current !== postMessageData) {
            // Only refetch if we have new postMessage data that hasn't been processed
            processedPostMessageRef.current = postMessageData;
            const heroImageMode = isHeroImageModeEnabled(params);
            if (heroImageMode) {
                handleHeroImageMode();
            } else {
                fetchMediaData({ 'params': params, 'spin': Number(params?.spin), 'catalog': Number(params?.catalog), 'feature_video': Number(params?.feature_video), 'isMobile': isMobile });
            }
        }
    }, [postMessageData]);

    // Separate effect for isMobile changes - only update carousel items, don't refetch data
    useEffect(() => {
        if (skuData && Object.keys(skuData).length > 0 && !loading) {
            // Re-transform existing data for mobile/desktop view without refetching
            const items = transformMediaDataToItems(skuData, isFirstSixExt, isMobile);
            const idx360 = items.findIndex(item => item.type === "360");
            const idx360Int = items.findIndex(item => item.type === "360Int");
            if (idx360 !== -1 && idx360Int !== -1 && idx360Int !== idx360 + 1) {
                const [intItem] = items.splice(idx360Int, 1);
                items.splice(idx360 + 1, 0, intItem);
            }
            setCarouselItems(items);
        }
    }, [isMobile]);
    return { skuData, loading, isFirstSixExt, selectedTemplate, selectedTab, setSelectedTab, tabs, carouselItems, dynamicTabImage, dealerVinId, enterpriseId, aspectRatio, skuName, mediaId, sessionId: sessionIdRef?.current, teamId, executionTime, apiCallFlag, isMobile, productFetchTimes, isHeroImageMode, heroImageIndex, postMessageData };
}

export default useMediaDataAndTabs;